<template>
  <div
    class="docs-visual-placeholder"
    :style="{ '--min-height': `${minHeight}px` }"
  >
    <img
      v-if="imgLoaded && imgSrc"
      :src="imgSrc"
      :alt="alt"
      class="docs-visual-placeholder__img"
      @error="handleImgError"
    />
    <div
      v-else
      role="img"
      :aria-label="alt"
      class="docs-visual-placeholder__fallback"
    >
      <KdsEyebrow class="docs-visual-placeholder__label">Visual coming soon</KdsEyebrow>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import KdsEyebrow from '@/kds/primitives/KdsEyebrow.vue';

interface Props {
  /** The docs section slug — resolved through `SECTION_IMAGE_MAP` to the real asset path. */
  section: string;
  /** Accessible description of the visual content. */
  alt: string;
  /** Minimum height of the placeholder in pixels. Default: 240. */
  minHeight?: number;
}

const props = withDefaults(defineProps<Props>(), {
  minHeight: 240
});

const imgLoaded = ref(true);

/**
 * Maps docs section slugs to their shipped image paths.
 * Filenames intentionally differ from UI slugs (clearer, content-driven names).
 * Unmapped sections fall back to the "Visual coming soon" block.
 */
const SECTION_IMAGE_MAP: Record<string, string> = {
  overview: '/assets/docs/main.jpg',
  'create-persona': '/assets/docs/personas.jpg',
  'create-segment': '/assets/docs/segments.jpg',
  'start-debate': '/assets/docs/debates.jpg',
  'read-verdict': '/assets/docs/verdicts.jpg'
};

const imgSrc = computed<string | undefined>(() => SECTION_IMAGE_MAP[props.section]);

const handleImgError = (): void => {
  imgLoaded.value = false;
};
</script>

<style scoped lang="scss">
.docs-visual-placeholder {
  width: 100%;
  min-height: var(--min-height, 240px);
  border: var(--border);
  border-radius: var(--r-4);
  background: var(--paper-2);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: var(--min-height, 240px);
  }

  &__label {
    color: var(--stone-2);
  }
}
</style>
